// client/src/services/teacherService.ts

import apiClient from "./api"; // Assumes you have a configured API client (e.g., using Axios)
import { api } from './api';

/** ---------------------------------------------------------------------------
 *  CLASS MANAGEMENT
 *  ---------------------------------------------------------------------------
 */

/**
 * Interface representing a Class.
 */
export interface Class {
  id: string;
  name: string;
  subject: string;
  schedule: string;
}

/**
 * Payload for creating a class.
 */
export interface CreateClassPayload {
  name: string;
  subject: string;
  schedule: string;
}

/**
 * Fetch all classes for the teacher.
 * GET /teacher/classes
 */
export const getClasses = async (): Promise<Class[]> => {
  try {
    const response = await apiClient.get("/teacher/classes");
    return response.data; // Adjust if your API returns a nested object
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/**
 * Create a new class.
 * POST /teacher/classes
 */
export const createClass = async (
  data: CreateClassPayload
): Promise<Class> => {
  try {
    const response = await apiClient.post("/teacher/classes", data);
    return response.data;
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/**
 * Delete an existing class by its ID.
 * DELETE /teacher/classes/:id
 */
export const deleteClass = async (
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete(`/teacher/classes/${id}`);
    return response.data;
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/** ---------------------------------------------------------------------------
 *  STUDENT ASSESSMENT
 *  ---------------------------------------------------------------------------
 */

/**
 * Interface representing an Assessment.
 */
export interface Assessment {
  id: string;
  title: string;
  description: string;
  dueDate: string;
}

/**
 * Payload for creating a student assessment.
 */
export interface CreateAssessmentPayload {
  title: string;
  description: string;
  dueDate: string;
}

/**
 * Fetch all assessments.
 * GET /teacher/assessments
 */
export const getAssessments = async (): Promise<Assessment[]> => {
  try {
    const response = await apiClient.get("/teacher/assessments");
    return response.data;
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/**
 * Create a new assessment.
 * POST /teacher/assessments
 */
export const createAssessment = async (
  data: CreateAssessmentPayload
): Promise<Assessment> => {
  try {
    const response = await apiClient.post("/teacher/assessments", data);
    return response.data;
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/** ---------------------------------------------------------------------------
 *  RESOURCE MANAGEMENT
 *  ---------------------------------------------------------------------------
 */

/**
 * Interface representing a Resource.
 */
export interface Resource {
  id: string;
  title: string;
  url: string;
}

/**
 * Payload for uploading a resource.
 */
export interface UploadResourcePayload {
  title: string;
  file: File;
}

/**
 * Fetch all resources for the teacher.
 * GET /teacher/resources
 */
export const getResources = async (): Promise<Resource[]> => {
  try {
    const response = await apiClient.get("/teacher/resources");
    return response.data;
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/**
 * Upload a new resource.
 * POST /teacher/resources
 */
export const uploadResource = async (
  data: UploadResourcePayload
): Promise<Resource> => {
  const formData = new FormData();
  formData.append("title", data.title);
  formData.append("file", data.file);

  try {
    const response = await apiClient.post("/teacher/resources", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/**
 * Delete a resource by its ID.
 * DELETE /teacher/resources/:id
 */
export const deleteResource = async (
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete(`/teacher/resources/${id}`);
    return response.data;
  } catch (error) {
    handleTeacherApiError(error);
    throw error;
  }
};

/** ---------------------------------------------------------------------------
 *  UTILITY: Error Handling
 *  ---------------------------------------------------------------------------
 */

const handleTeacherApiError = (error: unknown): void => {
  if (error instanceof Error) {
    console.error("Teacher Service Error:", error.message);
  } else {
    console.error("Unexpected Teacher Service Error:", error);
  }
};

export interface TeacherVerificationResponse {
  success: boolean;
  data: {
    message: string;
    data: {
      school: {
        id: string;
        name: string;
        status: string;
        paymentStatus: string;
      };
      teacher: {
        id: string;
        teacherId: string;
        status: string;
      };
    };
  };
}

export const verifyTeacherSchoolId = async (teacherId: string, schoolName: string): Promise<TeacherVerificationResponse> => {
  try {
    const response = await apiClient.get(`/teacher-verification/verify-school/${teacherId}`, {
      params: {
        schoolName
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Verification error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to verify teacher ID');
  }
};
